import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomAccountSelect, CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton, UIAmountInput } from "components/ui";
import { UITextClearInput } from "components/ui/UITextInput";
import Classes, { Account, TransferTemplate } from "model";
import React from "react";

import "./mobile.css";

/**
 * @function AddTransferTemplatePage
 * @param {STRING} accountFrom - Sets the account from text. 
 * @param {STRING} accountTo- Sets the account to text. 
 * @return {React.Component}
 * @exported true
 * @description The main component for AddTransferTemplatePage
 */
export default function AddTransferTemplatePage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };
    const { goStep, prevStep } = useWorkflow() ?? {};

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,
        
        onCancel: () => prevStep(-1),

        onChange: prop => val => {
            let _val = val;

            // Use the string "id" to change the "account" values since they are class type "Account"
            // but "Account.initAll()" initialized "CheckingAccount", "SavingsAccount", ect class types.
            if(prop === "fromAccount" || prop === "toAccount") _val = val.id;

            BCMSStateManagerCRUD.init(state)
              .update([ "data", "template", prop ], _val)
              .submit(setState);
        },

        onSave: () => {
            const nextID = TransferTemplate.nextID();
            let addedTemplate = TransferTemplate.init({ ...data.template.record, id: nextID });
            const defaultTemplateWithData = BCMSDefaultModel.transferTemplate();
            const mergedTemplate = BCMSDefaultModel.mergeRecords(addedTemplate, defaultTemplateWithData);
            addedTemplate = TransferTemplate.init({ ...mergedTemplate });
            
            BCMSRecord.batch([
                TransferTemplate.create({ ...mergedTemplate })
            ], context, Classes);

            goStep(0, { data: { addedTemplate: addedTemplate, templateAdded: true }, ui });
        }
    });

    return (
        <PageContainer className="add-transfer-template-page" state={ { data, ui } } controller={ controller }>
            <PageContent/>
        </PageContainer>
    )
}

export const getDefaultData = () => {
    const defaultTemplateRecord = { needsApproval: true, levelOfApprover: "0" };

    return {
        accounts: Account.initAllAsset(),
        template: TransferTemplate.init({ ...defaultTemplateRecord })
    };
};

export const getDefaultUI = () => ({
    from: { isActive: false },
    to: { isActive: false },
    levelOfApprover: { isActive: false }
});

function PageContent(props) {
    const { data, ui, onCancel, onChange, onSave } = useStateManager();
    const accounts = data.accounts ?? [];
    const template = data.template ?? {};

    return (
        <PageContentView>
                <CenterContentContainer>
                    <ContentHeaderView text="Add Template" />
                    <FormContainer className="small-form-width">
                        <FormRowView text="Template Name">
                            <UITextClearInput dataPoint={ `Template_Name_Input` } value={ template.name } onChange={ onChange("name") } />
                        </FormRowView>
                        <FormRowView text="From">
                            <CustomAccountSelect dataPoint={ `From_Select` } accounts={ accounts } onChange={ onChange("fromAccount") } value={ template.fromAccount ? Account.initByType(template.fromAccount.id) : "" } active={ ui.from.isActive } />
                        </FormRowView>
                        <FormRowView text="To">
                            <CustomAccountSelect dataPoint={ `To_Select` } accounts={ accounts } onChange={ onChange("toAccount") } value={ template.toAccount ? Account.initByType(template.toAccount.id) : "" } disabled={ template.fromAccount } active={ ui.to.isActive } />
                        </FormRowView>
                        <FormRowView text="Default Transfer Amount">
                            <UIAmountInput dataPoint={ `Default_Transfer_Input` } value={ template.defaultAmount.amount ?? ""  } onChange={ onChange("defaultAmount") } formatOnBlur={ false } />
                        </FormRowView>
                        <FormRowView text="Maximum Transfer Amount">
                            <UIAmountInput dataPoint={ `Maximum_Transfer_Input` } value={ template.maxAmount.amount ?? ""  } onChange={ onChange("maxAmount") } />
                        </FormRowView>
                        <FormRowView text="Note">
                            <UITextClearInput dataPoint={ `Note_Input` } value={ template.note  } onChange={ onChange("note") } />
                        </FormRowView>
                        <FormRowView text="Level Of Approver Required">
                            <CustomSelect dataPoint={ `Level_Of_Approver_Select` } value={ template.levelOfApprover } onChange={ onChange("levelOfApprover") } active={ ui.levelOfApprover.isActive }>
                                <CustomSelectItem dataPoint={ `Level_Of_Approver_Option_0` } value="0">0</CustomSelectItem>
                                <CustomSelectItem value="1" dataPoint={ `Level_Of_Approver_Option_1` }>1</CustomSelectItem>
                                <CustomSelectItem value="2" dataPoint={ `Level_Of_Approver_Option_2` }>2</CustomSelectItem>
                            </CustomSelect>
                        </FormRowView>
                        <FormButtonRow>
                            <SecondaryButton text="Cancel" onClick={ onCancel } />
                            <PrimaryButton dataPoint="Add_Template_Button" text="Add Template" onClick={ onSave } />
                        </FormButtonRow>
                    </FormContainer>
                </CenterContentContainer>
            </PageContentView>
    );
}